import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { STRIPE_CREATE_PAYMENT_INTENT, STRIPE_CREATE_SUBSCRIPTION } from '../../config/Config';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const StripePaymentForm = ({
  planCode,
  planName,
  planPrice,
  changeReason,
  referralCode,
  onSuccess,
  onCancel,
  userEmail,
  /** When set, use this client secret and only confirm the PaymentIntent (e.g. prorated upgrade). No subscription API call. */
  clientSecretProp = null,
  /** When true, on success call onSuccess() only; do not create subscription (webhook applies access). */
  confirmOnly = false
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(clientSecretProp || null);

  useEffect(() => {
    if (clientSecretProp) {
      setClientSecret(clientSecretProp);
      return;
    }
    createPaymentIntent();
  }, [planCode, clientSecretProp]);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(STRIPE_CREATE_PAYMENT_INTENT, {
        plan_code: planCode,
        currency: 'usd'
      });
      
      setClientSecret(response.data.clientSecret);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError('Failed to initialize payment. Please try again.');
      enqueueSnackbar('Failed to initialize payment', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        enqueueSnackbar(stripeError.message, { variant: 'error' });
      } else if (paymentIntent.status === 'succeeded') {
        if (confirmOnly && onSuccess) {
          onSuccess({ paymentIntent });
        } else {
          await createSubscription();
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
      enqueueSnackbar('Payment failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async () => {
    try {
      const subscriptionData = {
        plan_code: planCode,
        change_reason: changeReason || 'User initiated plan change'
      };
      
      // Add referral code if available
      if (referralCode) {
        subscriptionData.referral_code = referralCode;
      }
      
      // Add user email for registration flow
      if (userEmail) {
        subscriptionData.email = userEmail;
      }
      
      const response = await api.post(STRIPE_CREATE_SUBSCRIPTION, subscriptionData);
      
      enqueueSnackbar('Subscription created successfully!', { variant: 'success' });
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError('Payment successful but subscription creation failed. Please contact support.');
      enqueueSnackbar('Subscription creation failed', { variant: 'error' });
    }
  };

  if (!clientSecretProp && loading && !clientSecret) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        {confirmOnly ? 'Complete your upgrade payment' : 'Complete Your Subscription'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {planName && (confirmOnly ? `Upgrading to: ${planName}` : `Plan: ${planName} - $${planPrice}`)}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Card Details
        </Typography>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      {/* Stripe branding and security message */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Your credit card details are not stored on our site. It is handled by Stripe.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Powered by{' '}
          <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Stripe
          </Box>
        </Typography>
      </Box>

      <Box display="flex" gap={2}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={20} /> : confirmOnly ? 'Pay now' : `Pay $${planPrice}`}
        </Button>
        
        {onCancel && (
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default StripePaymentForm; 